/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { cloneDeep } from "lodash";
import React, { useCallback, useRef, useState } from "react";
import { toast, TypeOptions } from "react-toastify";
import { useRecoilValue } from "recoil";
import * as CampaignApi from "../api/Campaign";
import { CampaignActionAtom } from "../atoms/CampaignActionAtom";
import { TempCampaignAtom } from "../atoms/CampaignAtom";
import { UserAtom } from "../atoms/UserAtom";
import Builder from "../components/NewCampaign/Create/Builder/Builder";
import { validateDelay } from "../components/NewCampaign/Create/Form/Delay";
import { validateEmail } from "../components/NewCampaign/Create/Form/Email";
import FormRouter from "../components/NewCampaign/Create/Form/FormRouter";
import { validateManageTags } from "../components/NewCampaign/Create/Form/ManageTag";
import { validateWebhook } from "../components/NewCampaign/Create/Form/Webhook";
import TopBar, { getBody } from "../components/NewCampaign/Create/TopBar";
import SeoHead from "../components/SeoHead";
import { INodeTypes } from "../generated/Api";
import { Campaign, INode } from "../types/CampaignTypes";

const Body = styled.div`
  height: calc(100vh - 84px);
  width: 100%;
  margin-top: 84px;
  display: flex;
  flex-direction: row;
  position: relative;
`;

export interface CampaignValidationError {
  toastInfo: {
    toastText: string;
    toastType: TypeOptions;
  };
}

export type CampaignValidation =
  | { node: INode; error: null }
  | { node: null; error: CampaignValidationError };

const VALIDATION_FUNCTIONS: Partial<
  {
    [key in INodeTypes]: (
      node: INode,
      campaign?: Campaign
    ) => CampaignValidation;
  }
> = {
  webHook: validateWebhook,
  manageTag: validateManageTags,
  email: validateEmail,
  delayTimer: validateDelay,
};

export const genericValidateCampaign = async (tempCampaign: Campaign) => {
  let erroredNodes = [];
  let validationResults = tempCampaign.nodes.map((node) =>
    VALIDATION_FUNCTIONS[node.type](node, tempCampaign)
  );
  erroredNodes = validationResults.reduce(
    (acc: number[], currentNode: CampaignValidation, index: number) =>
      currentNode.error ? [...acc, index] : acc,
    []
  );

  if (tempCampaign.tagConditions.length === 0) {
    const startTagError = {
      node: null,
      error: {
        toastInfo: {
          toastText: "Please, select at least one tag to trigger your campaign",
          toastType: "info",
        },
      },
    };
    validationResults[-1] = startTagError as any;
    erroredNodes = [-1, ...erroredNodes];
  }
  if (
    !(
      tempCampaign.stopTagConditions === undefined ||
      (Array.isArray(tempCampaign.stopTagConditions) &&
        tempCampaign.stopTagConditions.length)
    )
  ) {
    const stopTagError = {
      node: null,
      error: {
        toastInfo: {
          toastText:
            "Please, select at least one tag to stop your campaign or remove the stop campaign step",
          toastType: "info",
        },
      },
    };
    validationResults[-2] = stopTagError as any;
    erroredNodes = [-2, ...erroredNodes];
  }
  if (tempCampaign.nodes.length === 0) {
    const noCampaignNodes = {
      node: null,
      error: {
        toastInfo: {
          toastText: "Please, create at least one node",
          toastType: "info",
        },
      },
    };
    validationResults[-3] = noCampaignNodes as any;
    erroredNodes = [-3, ...erroredNodes];
  }

  return [erroredNodes, validationResults];
};

export function useValidateCampaign() {
  const tempCampaign = useRecoilValue<Campaign | null>(TempCampaignAtom);
  const timeout = useRef(null);
  const [erroredNodeCards, setErroredNodeCards] = useState<number[]>([]);
  const action = useRecoilValue(CampaignActionAtom);
  const user = useRecoilValue(UserAtom);

  const saveCampaign = useCallback(
    async (campaignId, updatedCampaign) => {
      if (!updatedCampaign?._id || action !== "edit") return;
      try {
        const fontFamily = user?.emailOptions.font.family;
        const fontSize = user?.emailOptions.font.size;
        const maxWidth = user?.emailOptions.maxWidth;

        let copyUdatedCampaign = cloneDeep(updatedCampaign);

        copyUdatedCampaign.nodes = copyUdatedCampaign.nodes.map((node) => {
          if (node.type === INodeTypes.Email && node.email?.interestingURLs) {
            let interestingURLs = node.email.interestingURLs;
            let sanitizedInterestTags: { [key: string]: string[] } = {};
            if (interestingURLs) {
              Object.keys(interestingURLs).forEach((tag) => {
                sanitizedInterestTags[tag.split(".").join("|")] =
                  interestingURLs[tag];
              });
            }
            node.email.interestingURLs = sanitizedInterestTags;
            node.email.body = getBody(
              node.email.body,
              fontFamily,
              fontSize,
              maxWidth,
              false
            );
            return node;
          } else {
            return node;
          }
        });

        const response = await CampaignApi.updateCampaign(
          campaignId,
          copyUdatedCampaign
        );
        return response;
      } catch (error) {
        console.log(error);
        return { count: 0, result: null };
      }
    },
    [user, action]
  );

  const setErrors = (cards) => {
    if (timeout.current) clearTimeout(timeout.current);
    setErroredNodeCards(cards);
    setTimeout(() => {
      setErroredNodeCards([]);
    }, 2000);
  };

  const validateCampaign = async () => {
    if (!tempCampaign) return;
    const [erroredNodes, validationResults] = await genericValidateCampaign(
      tempCampaign
    );

    if (erroredNodes.length) {
      const { toastText, toastType } =
        validationResults[erroredNodes[0]].error.toastInfo;
      toast(toastText, { type: toastType });
      setErrors(erroredNodes);
    } else {
      setErrors([]);
    }

    return erroredNodes;
  };

  return { erroredNodeCards, validateCampaign, saveCampaign };
}

export default function CreateCamapign(): JSX.Element {
  const { erroredNodeCards, validateCampaign, saveCampaign } =
    useValidateCampaign();

  return (
    <>
      <SeoHead pageName="Campaign" />
      <TopBar validateCampaign={validateCampaign} saveCampaign={saveCampaign} />
      <Body>
        <Builder erroredCards={erroredNodeCards} />
        <FormRouter />
      </Body>
    </>
  );
}
